import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { InputHTMLAttributes } from 'react';

import { DirectoryService } from '@repo/api/directory/directoryService';
import { useGetDirectoryWithShared } from '@repo/api/file-system/useGetDirectoryWithShared';
import { FileService } from '@repo/api/file/fileService';
import { getIsFSEntityFile } from '@repo/lib/fs/getIsFSEntityFile';
import { useQueryClient } from '@tanstack/react-query';
import { UploadCloudIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { FsDragDropProvider, FsDragOverlay } from '@/components/features/files/dnd';
import FilePageContextMenu from '@/components/features/files/FilePageContextMenu';
import UploadFSEntires from '@/components/features/files/forms/UploadFSEntires';
import FsFilters from '@/components/features/files/FsFilters';
import FilePageForms from '@/components/features/files/layouts/files-page/FilePageForms';
import FilesPageContent from '@/components/features/files/layouts/files-page/FilesPageContent';
import FsPageHeader from '@/components/features/files/layouts/header/FsPageHeader';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { useFileUpload } from '@/hooks/file-upload';
import { useFsData, useFsKeyboardNavigation } from '@/hooks/fs';
import { useMediaQuery } from '@/hooks/media-query';
import { useCredentials } from '@/store/auth-store';
import { useFilesStorage } from '@/store/files-store';

export default function FilesPage() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { directoryId: defaultDirectoryId } = useCredentials();
  const location = useLocation();
  const navigate = useNavigate();
  const breakpoint = useMediaQuery('md');

  // Files storage state
  const { resetSelected, setPermissions, setFileToOpen, setOpenViewer } = useFilesStorage();

  const [openFileUploadDrawer, setOpenFileUploadDrawer] = useState<boolean>(false);

  const isSharedPath = useMemo<boolean>(() => location.pathname.includes('/shared'), [location.pathname]);
  const useSharedIndex = useMemo<boolean>(() => isSharedPath && id === undefined, [isSharedPath, id]);

  const directoryId = useMemo<string | undefined>(() => (useSharedIndex ? undefined : (id ?? defaultDirectoryId)), [useSharedIndex, id, defaultDirectoryId]);

  const getDirectory = useGetDirectoryWithShared({
    directoryId: directoryId,
    useSharedEndpoint: useSharedIndex,
  });

  const { dataModel, sorting, files, directories, dataLen, allItems } = useFsData(getDirectory.fsItems, { syncStore: true });

  // navigate between directories within group scope
  const handleNavigateTo = useCallback(
    (nextDirectoryId?: string) => {
      if (isSharedPath) {
        if (nextDirectoryId) {
          navigate(`/fs/shared/${nextDirectoryId}`);
        } else {
          navigate('/fs/shared');
        }
        return;
      }

      if (nextDirectoryId && nextDirectoryId !== defaultDirectoryId) {
        navigate(`/fs/dir/${nextDirectoryId}`);
        return;
      }

      navigate('/fs');
    },
    [isSharedPath, navigate, defaultDirectoryId]
  );

  // Handle opening an item (for keyboard navigation)
  const handleOpenItem = useCallback(
    (item: DirectoryPublic | FilePublicWithPermissions) => {
      if (getIsFSEntityFile(item)) {
        // Open file in viewer
        setFileToOpen(item as FilePublicWithPermissions);
        setOpenViewer(true);
      } else {
        // Navigate to directory
        handleNavigateTo((item as DirectoryPublic).id);
      }
    },
    [handleNavigateTo, setFileToOpen, setOpenViewer]
  );

  // Keyboard navigation hook
  useFsKeyboardNavigation({
    items: allItems,
    enabled: getDirectory.isSuccess && dataLen > 0,
    onOpen: handleOpenItem,
  });

  // switch between tabs
  const handleTabChange = (tab: string) => {
    if (tab === 'MY_DRIVE') {
      navigate('/fs');
      return;
    }
    navigate('/fs/shared');
  };

  const filesForViewer = useMemo<FilePublicWithPermissions[]>(() => files.map((row) => row.original), [files]);

  const [fileUploadState, fileUploadActions] = useFileUpload({
    uploadFileFn: async (file, options) => {
      const response = await FileService.uploadFile({
        file: file,
        directoryId: options.directory?.id || directoryId || '',
        abortController: options.abortController,
        progressCallBack: options.progressCallback,
      });
      return response.data;
    },
    createDirectoryFn: async (name, config) => {
      const response = await DirectoryService.createDirectory({
        name: name,
        parentDirId: config?.id || directoryId || '',
      });
      return response.data;
    },
    postponeUpload: false,
    onFilesChange: (files) => {
      if (files.length > 0) {
        setOpenFileUploadDrawer(true);
      }
    },
    onFileUploaded: () => {
      queryClient.invalidateQueries({ queryKey: ['directory', directoryId] });
    },
  });

  const fileInputProps = fileUploadActions.createInputProps({
    multiple: true,
  });

  const directoryInputProps = fileUploadActions.createInputProps({
    webkitdirectory: 'true',
  } as InputHTMLAttributes<HTMLInputElement>);

  const handleUploadFiles = useCallback(() => {
    fileInputProps.openFileDialog();
    setTimeout(() => setOpenFileUploadDrawer(true), 100);
  }, [fileInputProps, setOpenFileUploadDrawer]);

  const handleUploadDir = useCallback(() => {
    directoryInputProps.openFileDialog();
    setTimeout(() => setOpenFileUploadDrawer(true), 100);
  }, [directoryInputProps, setOpenFileUploadDrawer]);

  const isContextMenuDisabled = useMemo<boolean>(() => {
    return getDirectory.isError || getDirectory.isLoading || !breakpoint || directoryId === undefined;
  }, [getDirectory.isError, getDirectory.isLoading, breakpoint, directoryId]);

  // Set permissions when directory changes
  useEffect(() => {
    if (getDirectory.directoryData) {
      setPermissions(getDirectory.directoryData.permissions);
    }
  }, [getDirectory.directoryData, setPermissions]);

  // Reset selection when directory changes
  useEffect(() => {
    resetSelected();
  }, [directoryId, resetSelected]);

  return (
    <>
      <FilePageForms directoryId={directoryId ?? ''} files={filesForViewer} />
      <UploadFSEntires open={openFileUploadDrawer} onOpenChange={setOpenFileUploadDrawer} fileUploadState={fileUploadState} fileUploadActions={fileUploadActions} />
      <div className={'base-direction-col'} tabIndex={-1} {...fileUploadActions.createDropzoneProps().props}>
        {fileUploadState.isDragging && (
          <div
            className="bg-accent/75 pointer-events-none absolute top-0 right-0 bottom-0 left-0 z-50 flex flex-col items-center justify-center gap-2 backdrop-blur-xs select-none"
            aria-hidden="true"
            tabIndex={-1}
            role="presentation"
          >
            <FeaturedIcon size={'md'}>
              <UploadCloudIcon />
            </FeaturedIcon>
            <h4 className="text-foreground text-xl font-semibold">Upuść pliki tutaj</h4>
          </div>
        )}
        <FsPageHeader
          directory={getDirectory.directoryData}
          sorting={sorting}
          isLoading={getDirectory.isLoading}
          isError={getDirectory.isError}
          handleNavigateTo={handleNavigateTo}
          handleTabChange={handleTabChange}
          fsPageType={isSharedPath ? 'SHARED_DRIVE' : 'MY_DRIVE'}
          onUploadFilesOpen={handleUploadFiles}
          onUploadDirOpen={handleUploadDir}
          directoryId={directoryId}
        />
        <div className={'w-full'}>
          <FsFilters dataModel={dataModel} sorting={sorting} />
        </div>
        <FsDragDropProvider disabled={getDirectory.isPending} directoryId={directoryId ?? ''}>
          <div className={'h-full w-full'}>
            <FilePageContextMenu disabled={isContextMenuDisabled} onUploadFilesOpen={handleUploadFiles} onUploadDirOpen={handleUploadDir}>
              <FilesPageContent
                files={files}
                directories={directories}
                isEmpty={dataLen === 0}
                isSuccess={getDirectory.isSuccess}
                isLoading={getDirectory.isPending}
                isError={getDirectory.isError}
                directoryId={directoryId}
                errorMessage={getDirectory.errorMessage}
                handleNavigateTo={handleNavigateTo}
                onUploadFilesOpen={handleUploadFiles}
              />
            </FilePageContextMenu>
            <input {...fileInputProps.props} ref={fileInputProps.ref} className={'sr-only'} />
            <input {...directoryInputProps.props} ref={directoryInputProps.ref} className={'sr-only'} />
          </div>
          {/* Drag overlay for visual feedback */}
          <FsDragOverlay />
        </FsDragDropProvider>
      </div>
    </>
  );
}
